import { useState, useCallback, useRef } from 'react';
import { getApiToken } from '../../../../context/AuthContext';
import { Message, QuickAction, ToolCallState, DataCard, Widget } from './types';
import { getRegistryItemById } from '../CommandBar/registry';

const AI_CHAT_URL = '/api/ai-chat/message';
const AI_CHAT_FEEDBACK_URL = '/api/ai-chat/feedback';
const MESSAGE_LIMIT = 20;

const FRIENDLY_ERRORS: Record<string, string> = {
  token_expired:
    'Your session has expired. Please refresh the page and try again.',
  unauthorized: 'You are not authorized. Please log in and try again.',
  missing_body: 'Something went wrong. Please try again later.',
  invalid_json: 'Something went wrong. Please try again later.',
  invalid_request_type: 'Something went wrong. Please try again later.',
};

function friendlyError(raw: string): string {
  return FRIENDLY_ERRORS[raw] ?? raw;
}

function generateId(): string {
  return `msg_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

function createEmptyMessage(
  role: 'user' | 'assistant',
  content: string = '',
): Message {
  return {
    id: generateId(),
    role,
    content,
    toolCalls: [],
    dataCards: [],
    widgets: [],
    quickActions: [],
  };
}

function getMessagesForApi(
  messages: Message[],
): Array<{ role: string; content: string }> {
  return messages.map((m) => ({ role: m.role, content: m.content }));
}

function isRecordObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function tryParseDataCard(result: string): DataCard | null {
  try {
    const parsed = JSON.parse(result);
    if (
      parsed &&
      typeof parsed.title === 'string' &&
      Array.isArray(parsed.rows)
    ) {
      return parsed;
    }
  } catch {
    // not valid JSON or not a DataCard
  }
  return null;
}

export async function parseSSEStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  onEvent: (eventName: string, data: string) => void,
): Promise<void> {
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() ?? '';
    for (const part of parts) {
      let eventName = '';
      let data = '';
      for (const line of part.split('\n')) {
        if (line.startsWith('event: ')) eventName = line.slice(7);
        if (line.startsWith('data: ')) data = line.slice(6);
      }
      if (eventName && data) onEvent(eventName, data);
    }
  }
}

type SSEEventData = Record<string, unknown>;

function parseEventData(data: string): SSEEventData {
  try {
    return JSON.parse(data);
  } catch {
    return {};
  }
}

export function useAiChat(): {
  messages: Message[];
  isLoading: boolean;
  pendingConfirm: ToolCallState | null;
  error: string | null;
  activeToolName: string | null;
  sendMessage: (text: string) => void;
  confirmTool: (toolId: string) => void;
  cancelTool: (toolId: string) => void;
  reset: () => void;
  restoreMessages: (msgs: Message[]) => void;
  submitFeedback: (
    messageId: string,
    rating: 'up' | 'down',
    comment?: string,
  ) => Promise<void>;
  messageCount: number;
} {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeToolName, setActiveToolName] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  // Set when a tool finishes so the next text delta starts a new paragraph
  // instead of gluing the post-tool answer onto the pre-tool narration.
  const needsParagraphBreakRef = useRef(false);

  const pendingConfirm: ToolCallState | null =
    messages
      .flatMap((m) => m.toolCalls)
      .filter((tc) => tc.status === 'confirm_required')
      .pop() ?? null;

  const updateLastAssistantMessage = (
    updater: (msg: Message) => Message,
  ): void => {
    setMessages((prev) => {
      const lastIdx = prev.length - 1;
      if (lastIdx < 0 || prev[lastIdx].role !== 'assistant') return prev;
      const updated = [...prev];
      updated[lastIdx] = updater(updated[lastIdx]);
      return updated;
    });
  };

  const handleSSEEvent = useCallback(
    (eventName: string, rawData: string): void => {
      const data = parseEventData(rawData);

      switch (eventName) {
        case 'run_started': {
          const runId = typeof data.runId === 'string' ? data.runId : undefined;
          if (runId) {
            updateLastAssistantMessage((msg) => ({ ...msg, runId }));
          }
          break;
        }
        case 'text_delta': {
          const content = typeof data.content === 'string' ? data.content : '';
          const breakNeeded = needsParagraphBreakRef.current;
          needsParagraphBreakRef.current = false;
          updateLastAssistantMessage((msg) => ({
            ...msg,
            content:
              msg.content +
              (breakNeeded &&
              msg.content.length > 0 &&
              !msg.content.endsWith('\n\n')
                ? '\n\n'
                : '') +
              content,
          }));
          break;
        }
        case 'tool_start': {
          const toolName =
            typeof data.toolName === 'string' ? data.toolName : 'unknown';
          const toolCall: ToolCallState = {
            id: typeof data.id === 'string' ? data.id : generateId(),
            toolName,
            input: isRecordObject(data.input) ? data.input : {},
            status: 'running',
          };
          setActiveToolName(toolName);
          updateLastAssistantMessage((msg) => ({
            ...msg,
            toolCalls: [...msg.toolCalls, toolCall],
          }));
          break;
        }
        case 'tool_done': {
          setActiveToolName(null);
          needsParagraphBreakRef.current = true;
          const toolId = typeof data.id === 'string' ? data.id : '';
          const result = typeof data.result === 'string' ? data.result : '';
          const widget =
            isRecordObject(data.widget) &&
            typeof (data.widget as Record<string, unknown>).type === 'string'
              ? (data.widget as Widget)
              : null;
          updateLastAssistantMessage((msg) => {
            const dataCard = tryParseDataCard(result);
            return {
              ...msg,
              toolCalls: msg.toolCalls.map((tc) =>
                tc.id === toolId ? { ...tc, status: 'done', result } : tc,
              ),
              dataCards: dataCard
                ? [...msg.dataCards, dataCard]
                : msg.dataCards,
              widgets: widget ? [...msg.widgets, widget] : msg.widgets,
            };
          });
          break;
        }
        case 'tool_confirm_required': {
          const toolId = typeof data.id === 'string' ? data.id : generateId();
          const toolName =
            typeof data.toolName === 'string' ? data.toolName : 'unknown';
          const input = isRecordObject(data.input) ? data.input : {};
          const summary = typeof data.summary === 'string' ? data.summary : '';
          const widget =
            isRecordObject(data.widget) &&
            typeof (data.widget as Record<string, unknown>).type === 'string'
              ? (data.widget as Widget)
              : null;
          updateLastAssistantMessage((msg) => {
            const exists = msg.toolCalls.some((tc) => tc.id === toolId);
            const updatedWidgets = widget
              ? [...msg.widgets, widget]
              : msg.widgets;
            if (exists) {
              return {
                ...msg,
                widgets: updatedWidgets,
                toolCalls: msg.toolCalls.map((tc) =>
                  tc.id === toolId
                    ? {
                        ...tc,
                        status: 'confirm_required',
                        confirmSummary: summary,
                      }
                    : tc,
                ),
              };
            }
            return {
              ...msg,
              widgets: updatedWidgets,
              toolCalls: [
                ...msg.toolCalls,
                {
                  id: toolId,
                  toolName,
                  input,
                  status: 'confirm_required',
                  confirmSummary: summary,
                },
              ],
            };
          });
          setIsLoading(false);
          break;
        }
        case 'message_done': {
          setActiveToolName(null);
          type ServerQuickAction = {
            id: string;
            kind: 'inline' | 'goto';
            promptTemplate?: string;
          };
          const rawActions: ServerQuickAction[] = Array.isArray(
            data.quickActions,
          )
            ? data.quickActions
                .map((item: unknown): ServerQuickAction | null => {
                  if (typeof item === 'string') {
                    return { id: item, kind: 'goto' };
                  }
                  if (
                    item &&
                    typeof item === 'object' &&
                    typeof (item as { id?: unknown }).id === 'string'
                  ) {
                    const obj = item as {
                      id: string;
                      kind?: unknown;
                      promptTemplate?: unknown;
                    };
                    const kind = obj.kind === 'inline' ? 'inline' : 'goto';
                    const promptTemplate =
                      typeof obj.promptTemplate === 'string'
                        ? obj.promptTemplate
                        : undefined;
                    return {
                      id: obj.id,
                      kind,
                      ...(promptTemplate ? { promptTemplate } : {}),
                    };
                  }
                  return null;
                })
                .filter((a): a is ServerQuickAction => a !== null)
            : [];
          const quickActions: QuickAction[] = rawActions
            .map((raw) => {
              const entry = getRegistryItemById(raw.id);
              if (!entry) return null;
              const action: QuickAction = {
                id: entry.id,
                type: entry.type as 'action' | 'goto',
                kind: raw.kind,
                label: entry.label,
                route: entry.route,
              };
              if (entry.icon) {
                action.icon = entry.icon;
              }
              if (raw.promptTemplate) {
                action.promptTemplate = raw.promptTemplate;
              }
              return action;
            })
            .filter((a): a is QuickAction => a !== null);
          updateLastAssistantMessage((msg) => ({
            ...msg,
            quickActions,
          }));
          setIsLoading(false);
          break;
        }
        case 'error': {
          const raw =
            typeof data.message === 'string' ? data.message : 'Unknown error';
          setError(friendlyError(raw));
          setIsLoading(false);
          break;
        }
      }
    },
    [],
  );

  const processStream = useCallback(
    async (response: Response): Promise<void> => {
      if (!response.body) {
        setError('No response body');
        setIsLoading(false);
        return;
      }
      const reader = response.body.getReader();
      try {
        await parseSSEStream(reader, handleSSEEvent);
      } finally {
        reader.releaseLock();
      }
    },
    [handleSSEEvent],
  );

  const postAndStream = useCallback(
    async (body: Record<string, unknown>): Promise<void> => {
      const controller = new AbortController();
      abortControllerRef.current = controller;
      needsParagraphBreakRef.current = false;

      try {
        const token = getApiToken();
        const response = await fetch(AI_CHAT_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: token } : {}),
          },
          body: JSON.stringify(body),
          signal: controller.signal,
        });

        if (!response.ok) {
          setError(`Request failed with status ${response.status}`);
          setIsLoading(false);
          return;
        }

        await processStream(response);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          return;
        }
        setError(err instanceof Error ? err.message : 'Network error');
        setIsLoading(false);
      }
    },
    [processStream],
  );

  const sendMessage = useCallback(
    (text: string): void => {
      if (messages.length >= MESSAGE_LIMIT) {
        setError('Message limit reached (20 messages). Please reset the chat.');
        return;
      }

      setError(null);
      const userMessage = createEmptyMessage('user', text);
      const assistantMessage = createEmptyMessage('assistant');

      setMessages((prev) => [...prev, userMessage, assistantMessage]);
      setIsLoading(true);

      const apiMessages = [
        ...getMessagesForApi(messages),
        { role: 'user', content: text },
      ];

      postAndStream({
        type: 'message',
        message: text,
        messages: apiMessages,
      });
    },
    [messages, postAndStream],
  );

  const confirmTool = useCallback(
    (toolId: string): void => {
      const toolCall = messages
        .flatMap((m) => m.toolCalls)
        .find((tc) => tc.id === toolId && tc.status === 'confirm_required');
      if (!toolCall) return;

      setIsLoading(true);
      setError(null);

      setMessages((prev) =>
        prev.map((msg) => ({
          ...msg,
          toolCalls: msg.toolCalls.map((tc) =>
            tc.id === toolId ? { ...tc, status: 'submitted' as const } : tc,
          ),
        })),
      );

      // Append a new assistant message for the confirm response
      const assistantMessage = createEmptyMessage('assistant');
      setMessages((prev) => [...prev, assistantMessage]);

      postAndStream({
        type: 'confirm',
        toolId,
        messages: getMessagesForApi(messages),
      });
    },
    [messages, postAndStream],
  );

  const cancelTool = useCallback(
    (toolId: string): void => {
      const toolCall = messages
        .flatMap((m) => m.toolCalls)
        .find((tc) => tc.id === toolId && tc.status === 'confirm_required');
      if (!toolCall) return;

      setIsLoading(true);
      setError(null);

      setMessages((prev) =>
        prev.map((msg) => ({
          ...msg,
          toolCalls: msg.toolCalls.map((tc) =>
            tc.id === toolId ? { ...tc, status: 'cancelled' as const } : tc,
          ),
        })),
      );

      // Append a new assistant message for the cancel response
      const assistantMessage = createEmptyMessage('assistant');
      setMessages((prev) => [...prev, assistantMessage]);

      postAndStream({
        type: 'cancel',
        toolId,
        messages: getMessagesForApi(messages),
      });
    },
    [messages, postAndStream],
  );

  const submitFeedback = useCallback(
    async (
      messageId: string,
      rating: 'up' | 'down',
      comment?: string,
    ): Promise<void> => {
      const message = messages.find((m) => m.id === messageId);
      if (!message || !message.runId) return;
      setMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, feedback: rating } : m)),
      );
      try {
        const token = getApiToken();
        await fetch(AI_CHAT_FEEDBACK_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: token } : {}),
          },
          body: JSON.stringify({
            runId: message.runId,
            rating,
            ...(comment ? { comment } : {}),
          }),
        });
      } catch {
        // Non-blocking: we already optimistically marked the feedback.
      }
    },
    [messages],
  );

  const reset = useCallback((): void => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setMessages([]);
    setIsLoading(false);
    setError(null);
  }, []);

  const restoreMessages = useCallback((msgs: Message[]): void => {
    setMessages(msgs);
    setIsLoading(false);
    setError(null);
  }, []);

  return {
    messages,
    isLoading,
    pendingConfirm,
    error,
    activeToolName,
    sendMessage,
    confirmTool,
    cancelTool,
    reset,
    restoreMessages,
    submitFeedback,
    messageCount: messages.length,
  };
}
